import { useEffect, useState } from "react";
import { loadSettings, saveSettings } from "./lib/settings";
import { api } from "./lib/api";
import { useStore } from "./lib/store";
import Login from "./screens/Login";
import Main from "./screens/Main";
import ErrorBoundary from "./components/ErrorBoundary";
import TitleBar from "./components/TitleBar";
import Lightbox from "./components/Lightbox";
import OutboxBanner from "./components/OutboxBanner";
import MeldingenBanner from "./components/MeldingenBanner";
import UpdateBanner from "./components/UpdateBanner";
import MentionsDrawer from "./components/MentionsDrawer";
import SettingsModal from "./components/SettingsModal";
import { primePling } from "./lib/sound";
import { checkForUpdate } from "./lib/tauri";
import { isCapacitor } from "./lib/capacitor";

export default function App() {
  const [phase, setPhase] = useState<"loading" | "login" | "main">("loading");
  const setMe = useStore((s) => s.setMe);

  useEffect(() => {
    void (async () => {
      const settings = loadSettings();
      if (!settings.server_url || !settings.token) {
        setPhase("login");
        return;
      }
      try {
        const { user } = await api.me();
        setMe(user);
        setPhase("main");
      } catch {
        saveSettings({ token: null });
        setPhase("login");
      }
    })();
    void checkForUpdate();
  }, [setMe]);

  useEffect(() => {
    if (!isCapacitor()) return;
    document.documentElement.classList.add("capacitor-native");
    return () => document.documentElement.classList.remove("capacitor-native");
  }, []);

  // Eerste user-gesture na auto-login (token al bewaard) gebruiken om
  // audio-autoplay te ontgrendelen. Listener zet zichzelf na 1× uit.
  useEffect(() => {
    const onFirst = () => {
      primePling();
      window.removeEventListener("pointerdown", onFirst);
      window.removeEventListener("keydown", onFirst);
    };
    window.addEventListener("pointerdown", onFirst, { once: true });
    window.addEventListener("keydown", onFirst, { once: true });
    return () => {
      window.removeEventListener("pointerdown", onFirst);
      window.removeEventListener("keydown", onFirst);
    };
  }, []);

  // Globale vangnet: een bestand dat náást de drop-zone wordt losgelaten
  // (op de sidebar, titelbalk, etc.) zou anders door WebView2 in het
  // venster geladen worden en de hele SPA vervangen. preventDefault op
  // window-niveau blokkeert dat. De pane-overlay in Main.tsx vangt de
  // legitieme drops nog steeds op via z'n eigen handler.
  useEffect(() => {
    const block = (e: DragEvent) => {
      if (e.dataTransfer && Array.from(e.dataTransfer.types).includes("Files")) {
        e.preventDefault();
      }
    };
    window.addEventListener("dragover", block);
    window.addEventListener("drop", block);
    return () => {
      window.removeEventListener("dragover", block);
      window.removeEventListener("drop", block);
    };
  }, []);

  return (
    <ErrorBoundary>
      <div className="flex h-full flex-col">
        {/* Boven alles, ook boven de titelbalk: een nieuwe versie is het
            eerste wat je hoort te zien, en het staat er los van waar je in
            de app bent. */}
        <UpdateBanner />
        <TitleBar />
        <OutboxBanner />
        {phase === "main" && <MeldingenBanner />}
        <div className="min-h-0 flex-1">
          {phase === "loading" ? (
            <div className="flex h-full items-center justify-center text-slate-400">
              Bezig met laden…
            </div>
          ) : phase === "login" ? (
            <Login onLoggedIn={() => setPhase("main")} />
          ) : (
            <Main onLogout={() => setPhase("login")} />
          )}
        </div>
      </div>
      <Lightbox />
      <MentionsDrawer />
      <SettingsModal />
    </ErrorBoundary>
  );
}
